/**
 * A code the Gateway names (the usage type 5 is "בית משותף") shows as the name first and the
 * code in brackets. The name first keeps the right-to-left order stable, and a code without a
 * name shows alone. Every place that shows such a code uses this rule.
 *
 * The cell value stays the code, so a filter, a sort and an edit work on the code. The names
 * travel on the row, in `valueNames`, keyed by the field that holds the code.
 */

export const VALUE_NAMES_FIELD = 'valueNames'

type RowWithNames = {
  [VALUE_NAMES_FIELD]?: Record<string, unknown> | null
  [field: string]: unknown
}

const trimmed = (name: unknown): string =>
  typeof name === 'string' ? name.trim() : ''

const hasCode = (code: unknown): code is string | number =>
  code !== null && code !== undefined && String(code).trim() !== ''

/** "name (code)", the code alone for an unnamed code, and '' without a code. */
export const formatCodeName = (code: unknown, name?: unknown): string => {
  if (!hasCode(code)) return ''
  const label = trimmed(name)
  return label ? `${label} (${code})` : String(code)
}

/** The name the row keeps for the code in `field`, if it has one. */
export const valueNameOf = (
  row: RowWithNames | null | undefined,
  field: string
): string | undefined => trimmed(row?.[VALUE_NAMES_FIELD]?.[field]) || undefined

/** What a cell, an export and a copy show for `field` of `row`. */
export const formatRowCode = (
  row: RowWithNames | null | undefined,
  field: string,
  value: unknown = row?.[field]
): string => formatCodeName(value, valueNameOf(row, field))

/** The names of the resources that carry one, under each key and its camelCase alias. */
export const collectValueNames = (
  resources:
    | ReadonlyArray<{
        key?: string
        name?: string
        value?: unknown
        valueName?: string | null
      }>
    | undefined
): Record<string, string> | undefined => {
  const names: Record<string, string> = {}
  for (const resource of resources ?? []) {
    const key = resource.key || resource.name
    const name = trimmed(resource.valueName)
    if (!key || !name) continue
    names[key] = name
    names[
      key.replace(/_([a-z])/g, (_, letter: string) => letter.toUpperCase())
    ] = name
  }
  return Object.keys(names).length > 0 ? names : undefined
}

/** A grid cell for a column that holds a code: the code stays the value, the cell shows the name too. */
export const renderCodeCell = (params: {
  value?: unknown
  row?: RowWithNames
  field: string
}): string => formatRowCode(params.row, params.field, params.value) || '-'
